import { useMemo } from "react";

import {
  AnalyzeSitewideFrontendTinybirdRequestBodyType,
  DatasetId,
} from "@intelligems/ig-types";
import { useBuildSitewideAnalyticsPayload } from "@src/api/analytics/hooks/use-build-sitewide-analytics-payload";
import { wrapAnalyticsRequest } from "@src/api/analytics/utils";
import { analyticsApi } from "@src/api/api";
import { sitewideAnalyticsKeys } from "@src/api/queryKeys";
import { useSelector } from "@src/store";
import { useQuery } from "@tanstack/react-query";

import { useOrgTimezone } from "../organizations/get-organization";

const fetchSitewideAnalytics = async (
  data: AnalyzeSitewideFrontendTinybirdRequestBodyType
) =>
  wrapAnalyticsRequest(async () => {
    const resp = await analyticsApi.post(
      `/v2/analytics/sitewide-analysis`,
      data
    );
    return resp.data;
  });

export const useGetSitewideAnalytics = (
  datasetId: DatasetId,
  parameters?: Partial<AnalyzeSitewideFrontendTinybirdRequestBodyType>,
  enabled = true
) => {
  const sitewideDashboardFilters = useSelector(
    (state) => state.sitewideDashboardFilters
  );
  const timezone = useOrgTimezone();
  const { endTime, startTime } = sitewideDashboardFilters;
  const buildSitewidePayload = useBuildSitewideAnalyticsPayload();
  const data = useMemo(
    () => buildSitewidePayload.build(datasetId, parameters, timezone),
    [timezone, datasetId, parameters, sitewideDashboardFilters]
  );

  return useQuery<any>({
    enabled: enabled && !!data && !!endTime && !!startTime, // Blocks the query until this condition is truthy
    queryKey: [sitewideAnalyticsKeys.all(data)],
    queryFn: async () => fetchSitewideAnalytics(data),
  });
};
